import { etagFromRow, get, getVersioned, post, postVersioned, putVersioned, seg } from '../client';
import type { Coded, ListParams, Page, PurposeOfUse } from '../types/common';
import type { MedicationItem } from '../types/meds';
import type {
  CarePlan, CareGap, CareTeamCandidate, CreateEncounterRequest, CreateNoteRequest, CreateObservationRequest, CreateProblemRequest, Encounter, EncounterDiagnosis,
  EncounterNoteStatus, Episode, Note, NoteAddendumRequest, NoteCorrectRequest, NoteDiff, NoteListItem, NoteRetractRequest, NoteVersion, NoteVersionKind, Observation,
  ObservationTrend, PatientSummary, Problem, ProblemUpdateRequest, SubmitNoteForReviewRequest, TimelineEvent, TimelineKind, UpdateNoteRequest,
} from '../types/clinical';

type P = { purpose?: PurposeOfUse };
type Raw = Record<string, unknown>;


/* ------------------------------------------------------------------ */
/* Normalizers: backend DTO → UI types. Tolerate the mock/UI shape.    */
/* ------------------------------------------------------------------ */

const ENCOUNTER_STATUS = new Set(['planned', 'arrived', 'in_progress', 'finished', 'cancelled', 'entered_in_error']);

export function normalizeEncounter(raw: unknown): Encounter {
  const e = raw as Encounter & Raw;
  const status = e.status === 'canceled' ? 'cancelled' : ENCOUNTER_STATUS.has(e.status) ? e.status : 'planned';
  return { ...e, status, locationName: e.locationName ?? '', reason: e.reason ?? '', appointmentId: e.appointmentId ?? null, episodeId: e.episodeId ?? null };
}

/** Encounters that are the visit happening now (arrived = rooming / waiting; in_progress = with the provider). */
export const isActiveEncounter = (e: Pick<Encounter, 'status'>) => e.status === 'arrived' || e.status === 'in_progress';
/** Encounters a note or the sign sheet can attach to today. */
export const isOpenEncounter = (e: Pick<Encounter, 'status'>) => isActiveEncounter(e) || e.status === 'planned';
const ENC_RANK: Record<string, number> = { in_progress: 0, arrived: 1, planned: 2 };
export const encounterRank = (e: Pick<Encounter, 'status'>) => ENC_RANK[e.status] ?? 9;

/**
 * The backend Encounter DTO has no noteStatus (R-114): derive it from the patient's notes.
 * signed/attested/locked progress note → signed; review_required → review_required; draft → draft; else none.
 */
export function noteStatusForEncounter(notes: Pick<NoteListItem, 'encounterId' | 'status' | 'kind'>[], encounterId: string | null | undefined): EncounterNoteStatus {
  if (!encounterId) return 'none';
  const mine = notes.filter((n) => n.encounterId === encounterId && n.kind === 'progress');
  if (mine.some((n) => n.status === 'signed' || n.status === 'attested' || n.status === 'locked')) return 'signed';
  if (mine.some((n) => n.status === 'review_required')) return 'review_required';
  if (mine.some((n) => n.status === 'draft')) return 'draft';
  return 'none';
}

export function normalizeNote(raw: unknown): Note {
  const n = raw as Note & Raw;
  return {
    ...n,
    cosigner: n.cosigner ?? null,
    signedBy: n.signedBy ?? null,
    parentId: n.parentId ?? null,
    linked: Array.isArray(n.linked) ? n.linked : [],
    sections: Array.isArray(n.sections) ? n.sections : [],
    version: typeof n.version === 'number' ? n.version : Number(n.rowVersion ?? 1),
  };
}

const normNoteListItem = (raw: unknown): NoteListItem => {
  const n = raw as NoteListItem & Raw;
  return { ...n, parentId: n.parentId ?? null, signedAt: n.signedAt ?? null };
};

const VERSION_KIND: Record<string, NoteVersionKind> = {
  draft: 'draft', created: 'draft', create: 'draft', template: 'draft',
  autosave: 'autosave', autosaved: 'autosave', save: 'autosave', review_required: 'autosave', 'submit-for-review': 'autosave',
  'ai-accept': 'ai-accept', ai_accept: 'ai-accept', ai_accepted: 'ai-accept',
  signed: 'signed', sign: 'signed', successor: 'signed', released: 'signed',
  cosign: 'cosign', cosigned: 'cosign', attested: 'cosign',
  addendum: 'addendum', amendment: 'addendum', amend: 'addendum',
  correction: 'correction', correct: 'correction', corrected: 'correction',
  eie: 'eie', entered_in_error: 'eie', 'entered-in-error': 'eie', retraction: 'eie', retract: 'eie', retracted: 'eie',
};
const VERSION_WORD: Record<string, string> = {
  draft: 'Draft started', created: 'Draft started', autosave: 'Editing', autosaved: 'Editing', review_required: 'Sent for review', signed: 'Signed',
  cosign: 'Co-signed', successor: 'Linked record signed', addendum: 'Addendum', amendment: 'Amendment', correction: 'Correction', retraction: 'Retracted',
  entered_in_error: 'Entered in error', 'ai-accept': 'AI text accepted', ai_accept: 'AI text accepted',
};

export function normalizeNoteVersion(raw: unknown): NoteVersion {
  const v = raw as NoteVersion & Raw;
  const rawKind = String(v.rawKind ?? v.kind ?? '');
  const kind = VERSION_KIND[rawKind] ?? 'autosave';
  // Backend labels are just "Version N"; add what happened so the history reads.
  const word = VERSION_WORD[rawKind];
  const label = word && /^Version \d+$/.test(v.label ?? '') ? `${word} · v${v.number}` : v.label ?? `Version ${v.number}`;
  return { ...v, kind, rawKind, label };
}

/** Versions newest first (by number), regardless of server order (R-028). */
export const sortVersions = (items: NoteVersion[]) => [...items].sort((a, b) => b.number - a.number || b.at.localeCompare(a.at));

export function normalizeProblem(raw: unknown): Problem {
  const p = raw as Problem & Raw;
  return { ...p, severity: p.severity ?? undefined, note: p.note || undefined, needsReconciliation: !!p.needsReconciliation };
}

export function normalizeObservation(raw: unknown): Observation {
  const o = raw as Observation & Raw;
  return { ...o, encounterId: o.encounterId ?? null, interpretation: o.interpretation ?? null, referenceRange: o.referenceRange ?? null };
}

/** Newest effective time first; ties by recorded time then id (R-028: never trust server order to pick "latest"). */
export const byEffectiveDesc = (a: Pick<Observation, 'effectiveAt' | 'recordedAt' | 'id'>, b: Pick<Observation, 'effectiveAt' | 'recordedAt' | 'id'>) =>
  b.effectiveAt.localeCompare(a.effectiveAt) || (b.recordedAt ?? '').localeCompare(a.recordedAt ?? '') || b.id.localeCompare(a.id);

export function normalizeTrend(raw: unknown): ObservationTrend {
  const t = raw as ObservationTrend & Raw;
  return { ...t, range: t.range ?? null, points: [...(t.points ?? [])].sort((a, b) => a.at.localeCompare(b.at)) };
}

const TIMELINE_KINDS = new Set<TimelineKind>([
  'encounter', 'note', 'order', 'result', 'medication', 'problem', 'document', 'message', 'referral', 'appointment', 'allergy', 'observation', 'care_plan',
]);

export function normalizeTimelineEvent(raw: unknown): TimelineEvent {
  const e = raw as TimelineEvent & Raw;
  const kind = (TIMELINE_KINDS.has(e.kind) ? e.kind : 'other') as TimelineKind;
  const generic = !e.title || e.title === String(e.kind).replace(/_/g, ' ');
  return { ...e, kind, title: generic ? (kind === 'care_plan' ? 'Care plan' : kind === 'observation' ? 'Observation' : e.title || 'Record') : e.title };
}

const ALLERGY_STATUS = new Set(['list', 'nka', 'nkda', 'not-assessed', 'unknown']);

/** Best-effort shape guard for medication rows contributed by the medication module (R-034 / summary). */
function normSummaryMed(raw: unknown): MedicationItem {
  const m = raw as MedicationItem & Raw;
  const medication = (m.medication ?? (m as Raw).code ?? null) as Coded | null;
  const src = m.source as unknown;
  return {
    ...m,
    medication: medication ?? { system: '', code: '', display: String(m.name ?? 'Medication') },
    name: m.name ?? medication?.display ?? 'Medication',
    dose: m.dose ?? '',
    sig: m.sig ?? '',
    kind: m.kind ?? 'statement',
    status: m.status ?? 'active',
    source: typeof src === 'object' && src ? (src as MedicationItem['source']) : { kind: 'verified', label: typeof src === 'string' ? src : 'Recorded', at: null },
  };
}

export function normalizeSummary(raw: unknown): PatientSummary {
  const s = raw as PatientSummary & Raw;
  const status = String((s.allergies as Raw | undefined)?.status ?? 'not-assessed');
  return {
    ...s,
    alerts: (s.alerts ?? []).map((a) => ({
      ...a,
      priority: (['critical', 'urgent', 'action', 'info'].includes(a.priority as string) ? a.priority : 'urgent') as typeof a.priority,
      // Backend links are API paths (/patients/{id}); map them to the chart.
      link: a.link?.startsWith('/patients/') ? `/chart/${a.link.slice('/patients/'.length).split('/')[0]}/vitals` : a.link,
    })),
    problems: (s.problems ?? []).map(normalizeProblem),
    medications: (s.medications ?? []).map(normSummaryMed),
    allergies: { status: (ALLERGY_STATUS.has(status) ? status : 'unknown') as PatientSummary['allergies']['status'], items: (s.allergies?.items ?? []) as PatientSummary['allergies']['items'] },
    vitals: (s.vitals ?? []).map((v) => ({ ...v, latest: v.latest ? normalizeObservation(v.latest) : null, trend: v.trend ?? [] })),
    recentResults: s.recentResults ?? [],
    careGaps: s.careGaps ?? [],
    notes: Array.isArray(s.notes) ? s.notes.map(normNoteListItem) : undefined,
    encounter: s.encounter ? normalizeEncounter(s.encounter) : null,
    restricted: s.restricted ?? { sections: [], count: 0 },
  };
}

const mapPage = <T>(p: Page<unknown>, f: (x: unknown) => T): Page<T> => ({ ...p, items: (p.items ?? []).map(f) });
const versioned = <T>(r: { data: unknown; etag: string | null }, f: (x: unknown) => T) => {
  const data = f(r.data);
  return { data, etag: r.etag ?? etagFromRow((data as { rowVersion?: number }).rowVersion) };
};

/** Same calendar day in local time (Today / "this visit"). */
const sameLocalDay = (iso: string, day: string) => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` === day;
};

/* ------------------------------------------------------------------ */

export const clinicalApi = {
  summary: async (patientId: string, o: P = {}) => normalizeSummary(await get<unknown>(`/patients/${seg(patientId)}/summary`, undefined, o)),
  timeline: async (patientId: string, p: ListParams & { kind?: string } = {}, o: P = {}) => {
    const page = mapPage(await get<Page<unknown>>(`/patients/${seg(patientId)}/timeline`, { ...p }, o), normalizeTimelineEvent);
    return { ...page, items: [...page.items].sort((a, b) => b.at.localeCompare(a.at)) };
  },
  episodes: (patientId: string) => get<Page<Episode>>('/episodes', { patientId }),
  createEpisode: (body: Pick<Episode, 'patientId' | 'type'>) => post<Episode>('/episodes', body),
  /**
   * GET /encounters accepts patientId, status, limit, cursor — NOT `date` (422). A `date` (YYYY-MM-DD) is applied
   * client-side to the returned page so older callers keep working; prefer appointment.encounterId for Today.
   */
  encounters: async (p: ListParams & { patientId?: string; date?: string; status?: string } = {}) => {
    const { date, ...q } = p;
    const page = mapPage(await get<Page<unknown>>('/encounters', { ...q }), normalizeEncounter);
    return date ? { ...page, items: page.items.filter((e) => sameLocalDay(e.start, date)) } : page;
  },
  createEncounter: async (body: CreateEncounterRequest) => normalizeEncounter(await post<unknown>('/encounters', body, { idempotent: true })),
  encounter: async (id: string) => versioned(await getVersioned<unknown>(`/encounters/${seg(id)}`), normalizeEncounter),
  /** planned → arrived → in_progress in one step (backend auto-arrives a planned visit). Needs the encounter ETag. */
  startEncounter: async (id: string, etag: string | null) =>
    versioned(await postVersioned<unknown>(`/encounters/${seg(id)}:start`, {}, { ifMatch: etag, idempotent: true }), normalizeEncounter),
  closeEncounter: async (id: string, etag: string | null) =>
    versioned(await postVersioned<unknown>(`/encounters/${seg(id)}:close`, {}, { ifMatch: etag, idempotent: true }), normalizeEncounter),
  diagnoses: (encounterId: string) => get<Page<EncounterDiagnosis>>(`/encounters/${seg(encounterId)}/diagnoses`),
  /** Workforce members who may be proposed for this patient's care team (excludes you and current members). */
  careTeamCandidates: (patientId: string) => get<Page<CareTeamCandidate>>(`/patients/${seg(patientId)}/care-team/candidates`),
  addDiagnosis: (encounterId: string, body: { code: Coded; rank?: number; problemId?: string | null }) =>
    post<EncounterDiagnosis>(`/encounters/${seg(encounterId)}/diagnoses`, body),
};

const ncmd = (id: string, c: string) => `/notes/${seg(id)}:${c}`;
const nv = (r: { data: unknown; etag: string | null }) => versioned(r, normalizeNote);

/** Use the caller's ETag; if it has none, read the note's current ETag so the command never goes out without If-Match. */
async function noteTag(id: string, etag: string | null | undefined): Promise<string | null> {
  if (etag) return etag;
  const r = await getVersioned<unknown>(`/notes/${seg(id)}`);
  return r.etag ?? etagFromRow((r.data as Note).rowVersion);
}

export const notesApi = {
  list: async (patientId: string, p: ListParams & { status?: string } = {}, o: P = {}) =>
    mapPage(await get<Page<unknown>>(`/patients/${seg(patientId)}/notes`, { ...p }, o), normNoteListItem),
  create: async (body: CreateNoteRequest) => nv(await postVersioned<unknown>('/notes', body, { idempotent: true })),
  get: async (id: string, o: P = {}) => nv(await getVersioned<unknown>(`/notes/${seg(id)}`, undefined, o)),
  /** Autosave. Requires If-Match; a stale ETag returns 412 ETNAS_VERSION_CONFLICT. */
  save: async (id: string, body: UpdateNoteRequest, etag: string | null) => nv(await putVersioned<unknown>(`/notes/${seg(id)}`, body, { ifMatch: etag })),
  /**
   * draft → review_required. `cosignerId` names the provider who will review and sign (R-026); that provider then
   * calls :sign (not :cosign) on the review_required note.
   */
  submitForReview: async (id: string, etag: string | null, body: SubmitNoteForReviewRequest = {}) =>
    nv(await postVersioned<unknown>(ncmd(id, 'submit-for-review'), body.cosignerId ? { cosignerId: body.cosignerId } : {}, { ifMatch: etag, idempotent: true })),
  /** Author signs a draft, or the named reviewer signs a review_required note. */
  sign: async (id: string, etag: string | null, label: string, attestation: string) =>
    nv(await postVersioned<unknown>(ncmd(id, 'sign'), { attestation }, { ifMatch: etag, idempotent: true, stepUpLabel: label })),
  /** signed → attested by the named cosigner (who is neither the author nor the signer). */
  cosign: async (id: string, etag: string | null, label: string, attestation?: string) =>
    nv(await postVersioned<unknown>(ncmd(id, 'cosign'), attestation ? { attestation } : {}, { ifMatch: etag, idempotent: true, stepUpLabel: label })),
  addendum: async (id: string, body: NoteAddendumRequest, label: string, etag?: string | null) =>
    nv(await postVersioned<unknown>(ncmd(id, 'addendum'), body, { ifMatch: await noteTag(id, etag), idempotent: true, stepUpLabel: label })),
  correct: async (id: string, body: NoteCorrectRequest, label: string, etag?: string | null) =>
    nv(await postVersioned<unknown>(ncmd(id, 'correct'), body, { ifMatch: await noteTag(id, etag), idempotent: true, stepUpLabel: label })),
  retract: async (id: string, body: NoteRetractRequest, label: string, etag?: string | null) =>
    nv(await postVersioned<unknown>(ncmd(id, 'retract'), body, { ifMatch: await noteTag(id, etag), idempotent: true, stepUpLabel: label })),
  enteredInError: async (id: string, body: NoteRetractRequest, label: string, etag?: string | null) =>
    nv(await postVersioned<unknown>(ncmd(id, 'entered-in-error'), body, { ifMatch: await noteTag(id, etag), idempotent: true, stepUpLabel: label })),
  /** R-135: signed/attested/locked → linked signed amendment ({reason,text}); step-up. */
  amend: async (id: string, body: { reason: string; text: string }, label: string, etag?: string | null) =>
    nv(await postVersioned<unknown>(ncmd(id, 'amend'), body, { ifMatch: await noteTag(id, etag), idempotent: true, stepUpLabel: label })),
  /** R-135: signed/attested → locked, by the author or the signer; step-up. */
  lock: async (id: string, body: { reason: string }, label: string, etag?: string | null) =>
    nv(await postVersioned<unknown>(ncmd(id, 'lock'), body, { ifMatch: await noteTag(id, etag), idempotent: true, stepUpLabel: label })),
  /** Newest first by version number (R-028). */
  versions: async (id: string) => {
    const page = mapPage(await get<Page<unknown>>(`/notes/${seg(id)}/versions`, { limit: 100 }), normalizeNoteVersion);
    return { ...page, items: sortVersions(page.items) };
  },
  diff: (id: string, v: string, against: string) => get<NoteDiff>(`/notes/${seg(id)}/versions/${seg(v)}/diff`, { against }),
};

async function problemTag(id: string, etag: string | null | undefined): Promise<string | null> {
  if (etag) return etag;
  const r = await getVersioned<unknown>(`/problems/${seg(id)}`);
  return r.etag ?? etagFromRow((r.data as Problem).rowVersion);
}

export const problemsApi = {
  list: async (patientId: string, p: ListParams & { status?: string } = {}, o: P = {}) =>
    mapPage(await get<Page<unknown>>(`/patients/${seg(patientId)}/problems`, { ...p }, o), normalizeProblem),
  create: async (patientId: string, body: CreateProblemRequest) =>
    normalizeProblem(await post<unknown>(`/patients/${seg(patientId)}/problems`, body, { idempotent: true })),
  /** Each command needs If-Match (problem ETag / `W/"<rowVersion>"`) and an Idempotency-Key. */
  resolve: async (id: string, reason?: string, etag?: string | null) =>
    normalizeProblem(await post<unknown>(`/problems/${seg(id)}:resolve`, reason ? { reason } : {}, { ifMatch: await problemTag(id, etag), idempotent: true })),
  refute: async (id: string, reason: string, etag?: string | null) =>
    normalizeProblem(await post<unknown>(`/problems/${seg(id)}:refute`, { reason }, { ifMatch: await problemTag(id, etag), idempotent: true })),
  reconcile: async (id: string, decision: 'accept' | 'reject' | 'defer', reason?: string, etag?: string | null) =>
    normalizeProblem(await post<unknown>(`/problems/${seg(id)}:reconcile`, reason ? { decision, reason } : { decision }, { ifMatch: await problemTag(id, etag), idempotent: true })),
  /** R-135: suspected/confirmed/refuted → entered_in_error ({reason}). */
  enteredInError: async (id: string, reason: string, etag?: string | null) =>
    normalizeProblem(await post<unknown>(`/problems/${seg(id)}:entered-in-error`, { reason }, { ifMatch: await problemTag(id, etag), idempotent: true })),
  /** R-135: correct onset/abatement/severity/note with a reason (verification unchanged). */
  update: async (id: string, body: ProblemUpdateRequest, etag?: string | null) =>
    normalizeProblem(await post<unknown>(`/problems/${seg(id)}:update`, body, { ifMatch: await problemTag(id, etag), idempotent: true })),
};

async function observationTag(id: string, etag: string | null | undefined): Promise<string | null> {
  if (etag) return etag;
  const r = await getVersioned<unknown>(`/observations/${seg(id)}`);
  return r.etag ?? etagFromRow((r.data as Observation).rowVersion);
}

export const observationsApi = {
  /** Sorted newest effective time first (R-028) so `items[0]` per code is the latest reading. */
  list: async (patientId: string, p: ListParams & { category?: string; code?: string; includeEnteredInError?: boolean } = {}, o: P = {}) => {
    const page = mapPage(await get<Page<unknown>>(`/patients/${seg(patientId)}/observations`, { ...p }, o), normalizeObservation);
    return { ...page, items: [...page.items].sort(byEffectiveDesc) };
  },
  /** 422 ETNAS_VALIDATION with errors[].code === 'IMPLAUSIBLE_VALUE' → resend with implausibleOverride.reason */
  create: async (patientId: string, body: CreateObservationRequest, idempotencyKey?: string) =>
    normalizeObservation(await post<unknown>(`/patients/${seg(patientId)}/observations`, body, { idempotent: idempotencyKey ?? true })),
  /** Needs If-Match (`W/"<rowVersion>"` of the observation being corrected). */
  correct: async (id: string, body: CreateObservationRequest & { reason: string }, etag?: string | null) =>
    normalizeObservation(await post<unknown>(`/observations/${seg(id)}:correct`, body, { ifMatch: await observationTag(id, etag), idempotent: true })),
  enteredInError: async (id: string, reason: string, etag?: string | null) =>
    normalizeObservation(await post<unknown>(`/observations/${seg(id)}:entered-in-error`, { reason }, { ifMatch: await observationTag(id, etag), idempotent: true })),
  /** Points oldest → newest (R-028). */
  trend: async (patientId: string, code: string) => normalizeTrend(await get<unknown>(`/patients/${seg(patientId)}/observations/trend`, { code })),
};

export const carePlansApi = {
  list: (patientId: string) => get<Page<CarePlan>>(`/patients/${seg(patientId)}/care-plans`),
  create: (patientId: string, body: Pick<CarePlan, 'title' | 'goals' | 'activities'>) => post<CarePlan>(`/patients/${seg(patientId)}/care-plans`, body),
  gaps: (patientId: string) => get<Page<CareGap>>(`/patients/${seg(patientId)}/care-gaps`),
};
